import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../lib/api";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Building2, CreditCard, Users, RefreshCw, AlertTriangle, UserCheck, Ban, ShieldCheck, X, KeyRound, Eye, EyeOff } from "lucide-react";

export default function AdminOrgDetail() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overridePlan, setOverridePlan] = useState("");
  const [overrideExpiresAt, setOverrideExpiresAt] = useState("");

  // Shopify Custom App (beta)
  const [shopifyClientId, setShopifyClientId]       = useState("");
  const [shopifyClientSecret, setShopifyClientSecret] = useState("");
  const [showSecret, setShowSecret]                 = useState(false);
  const [shopifySaving, setShopifySaving]           = useState(false);

  const token = localStorage.getItem("token");
  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchOrg = async () => {
    try {
      const res = await axios.get(`${API}/admin/organizations/${orgId}`, { headers: authHeader });
      setOrg(res.data);
    } catch (err) {
      toast.error("Error loading organization");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/admin/plans`, { headers: authHeader });
      setPlans(res.data?.plans || res.data || []);
    } catch (_) {}
  };

  useEffect(() => { fetchOrg(); fetchPlans(); }, [orgId]);

  // Initialise Shopify fields once org loads
  useEffect(() => {
    if (org) setShopifyClientId(org.shopify_client_id || "");
  }, [org]);

  const handleAction = async (action, body) => {
    setActionLoading(action);
    try {
      await axios.patch(`${API}/admin/organizations/${orgId}`, body, { headers: authHeader });
      toast.success(`Acción "${action}" completada`);
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantOverride = async () => {
    if (!overridePlan) { toast.error("Selecciona un plan"); return; }
    await handleAction("override", {
      plan_override: overridePlan,
      plan_override_expires_at: overrideExpiresAt || null,
    });
    setShowOverrideModal(false);
    setOverridePlan("");
    setOverrideExpiresAt("");
  };

  const handleSaveShopifyCustomApp = async () => {
    setShopifySaving(true);
    try {
      const body = { shopify_client_id: shopifyClientId || "" };
      // Only send secret if the user typed something new
      if (shopifyClientSecret) body.shopify_client_secret = shopifyClientSecret;
      await axios.patch(`${API}/admin/organizations/${orgId}`, body, { headers: authHeader });
      toast.success("Credenciales Custom App guardadas");
      setShopifyClientSecret("");
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setShopifySaving(false);
    }
  };

  const handleClearShopifyCustomApp = async () => {
    setShopifySaving(true);
    try {
      await axios.patch(`${API}/admin/organizations/${orgId}`,
        { shopify_client_id: "", shopify_client_secret: "" },
        { headers: authHeader }
      );
      toast.success("Credenciales Custom App eliminadas");
      setShopifyClientId("");
      setShopifyClientSecret("");
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally {
      setShopifySaving(false);
    }
  };

  const handleImpersonate = async (userId) => {
    setActionLoading("impersonate");
    try {
      const res = await axios.post(`${API}/admin/impersonate/${userId}`, {}, { headers: authHeader });
      const adminUserId = localStorage.getItem("active_user_id");
      const adminOrgId = adminUserId ? localStorage.getItem(`active_org_id:${adminUserId}`) : null;
      const adminSessionCookie = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('session_token='));
      localStorage.setItem("impersonation_admin_context", JSON.stringify({
        user_id: adminUserId, org_id: adminOrgId,
        session_cookie: adminSessionCookie ? adminSessionCookie.split('=')[1] : null
      }));
      localStorage.setItem("impersonation_token", res.data.token);
      localStorage.setItem("impersonation_admin_token", token);
      localStorage.setItem("impersonation_log_id", res.data.impersonation_log_id);
      localStorage.setItem("impersonation_target", JSON.stringify(res.data.target_user));
      localStorage.setItem("auth_token", res.data.token);
      localStorage.setItem("token", res.data.token);
      document.cookie = "session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      const { clearAuthContext } = await import("../lib/api");
      clearAuthContext();
      localStorage.setItem("active_user_id", res.data.target_user.user_id);
      toast.success(`Impersonando a ${res.data.target_user.email}`);
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al impersonar");
      setActionLoading(null);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800" /></div>;
  if (!org) return <p className="text-red-500">Organization not found</p>;

  const isSuspended = !!org.suspended_at;
  const hasOverride = !!org.plan_override;
  const overrideExpired = hasOverride && org.plan_override_expires_at && new Date(org.plan_override_expires_at) < new Date();

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-org-detail">
      {/* Override Modal */}
      {showOverrideModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Dar acceso manual</h2>
              <button onClick={() => setShowOverrideModal(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Plan</label>
                <select
                  value={overridePlan}
                  onChange={e => setOverridePlan(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="override-plan-select"
                >
                  <option value="">Selecciona un plan...</option>
                  {plans.map(p => (
                    <option key={p.plan_key} value={p.plan_key}>{p.name} ({p.plan_key})</option>
                  ))}
                  <option value="admin_access">Acceso admin (sin plan específico)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Fecha fin del acceso <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <input
                  type="date"
                  value={overrideExpiresAt}
                  onChange={e => setOverrideExpiresAt(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="override-expires-at-input"
                />
                <p className="text-xs text-slate-400 mt-1">Sin fecha = acceso indefinido</p>
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <Button
                className="flex-1 bg-slate-900 hover:bg-slate-700 text-white"
                onClick={handleGrantOverride}
                disabled={!!actionLoading || !overridePlan}
                data-testid="override-confirm-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                {actionLoading === "override" ? "Guardando..." : "Dar acceso"}
              </Button>
              <Button variant="outline" onClick={() => setShowOverrideModal(false)} className="flex-1">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/organizations")} data-testid="admin-org-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <h1 className="text-2xl font-semibold text-slate-900">{org.name}</h1>
        {isSuspended && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">Suspendida</span>}
        {hasOverride && !overrideExpired && <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">Acceso manual</span>}
        {overrideExpired && <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-xs font-medium">Override expirado</span>}
      </div>

      {/* Subscription info */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><CreditCard className="w-4 h-4" /> Suscripción</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Plan" value={org.plan_key || "Sin plan"} />
            <Row label="Estado" value={org.subscription_status || "none"} />
            <Row label="Stripe Customer" value={org.stripe_customer_id || "-"} mono />
            <Row label="Stripe Subscription" value={org.stripe_subscription_id || "-"} mono />
            <Row label="Fin de período" value={org.current_period_end ? new Date(org.current_period_end).toLocaleDateString("es-ES") : "-"} />
            <Row label="Cancelación programada" value={org.cancel_at_period_end ? "Sí" : "No"} />
            {hasOverride && (
              <>
                <div className="border-t pt-2 mt-2">
                  <Row label="Plan override" value={org.plan_override} />
                  <Row
                    label="Acceso hasta"
                    value={org.plan_override_expires_at
                      ? new Date(org.plan_override_expires_at).toLocaleDateString("es-ES")
                      : "Indefinido"}
                  />
                  {org.plan_override_granted_at && (
                    <Row label="Concedido" value={new Date(org.plan_override_granted_at).toLocaleDateString("es-ES")} />
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Building2 className="w-4 h-4" /> Organización</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="ID" value={org.organization_id} mono />
            <Row label="Owner" value={org.owner?.email || "-"} />
            <Row label="Creado" value={org.created_at ? new Date(org.created_at).toLocaleDateString("es-ES") : "-"} />
            <Row label="Pedidos (año)" value={org.order_count_year?.toLocaleString("es-ES") || "0"} />
            <Row label="Grandfather" value={org.grandfather_until ? new Date(org.grandfather_until).toLocaleDateString("es-ES") : "-"} />
            {isSuspended && <Row label="Motivo suspensión" value={org.suspension_reason || "-"} />}
          </CardContent>
        </Card>
      </div>

      {/* Connected shops */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Tienda conectada</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          {org.prestashop_config && <Row label="PrestaShop" value={org.prestashop_config.shop_url || "Configurado"} />}
          {org.shopify_config && <Row label="Shopify" value={org.shopify_config.shop_domain || "Configurado"} />}
          {!org.prestashop_config && !org.shopify_config && <p className="text-slate-400">Sin tienda conectada</p>}
          {org.products_sync && <Row label="Última sync productos" value={org.products_sync.last_sync || "-"} />}
          {org.orders_sync && <Row label="Última sync pedidos" value={org.orders_sync.synced_at || "-"} />}
        </CardContent>
      </Card>

      {/* Shopify Custom App credentials (beta) */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-slate-500" />
            Shopify Custom App
            {org.shopify_client_id
              ? <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Activa</span>
              : <span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Sin configurar (usa app pública)</span>
            }
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p className="text-slate-500 text-xs">
            Para clientes beta conectados con Custom Apps de Shopify Partners. Si se rellenan estos campos,
            el flujo OAuth usará estas credenciales en lugar de las globales del servidor.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Client ID</label>
              <input
                type="text"
                value={shopifyClientId}
                onChange={e => setShopifyClientId(e.target.value)}
                placeholder="p.ej. a1b2c3d4e5f6..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="shopify-custom-client-id"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Client Secret
                <span className="ml-1 text-slate-400 font-normal">(se guarda cifrado)</span>
              </label>
              <div className="relative">
                <input
                  type={showSecret ? "text" : "password"}
                  value={shopifyClientSecret}
                  onChange={e => setShopifyClientSecret(e.target.value)}
                  placeholder={org.shopify_client_id ? "••••••••••••• (ya guardado)" : "Introduce el nuevo secret"}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 pr-9 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="shopify-custom-client-secret"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              className="bg-slate-900 hover:bg-slate-700 text-white"
              onClick={handleSaveShopifyCustomApp}
              disabled={shopifySaving}
              data-testid="shopify-custom-save-btn"
            >
              {shopifySaving ? "Guardando..." : "Guardar credenciales"}
            </Button>
            {org.shopify_client_id && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleClearShopifyCustomApp}
                disabled={shopifySaving}
                data-testid="shopify-custom-clear-btn"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Eliminar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Miembros ({org.members?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {org.members?.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {org.members.map(m => (
                <div key={m.user_id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{m.email}</p>
                    <p className="text-xs text-slate-400">{m.name || "-"} &middot; {m.org_role || "member"}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleImpersonate(m.user_id)} disabled={!!actionLoading} data-testid={`impersonate-${m.user_id}`}>
                    <UserCheck className="w-3 h-3 mr-1" />
                    {actionLoading === "impersonate" ? "..." : "Impersonar"}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-sm">Sin miembros</p>
          )}
        </CardContent>
      </Card>

      {/* Recent errors */}
      {org.recent_errors?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-red-600"><AlertTriangle className="w-4 h-4" /> Errores recientes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-xs">
              {org.recent_errors.map((err, i) => (
                <div key={i} className="bg-red-50 rounded p-2">
                  <p className="font-mono text-red-700">{err.error || err.message || JSON.stringify(err)}</p>
                  <p className="text-red-400 mt-1">{err.timestamp || ""}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin actions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Acciones de administrador</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {isSuspended ? (
            <Button variant="outline" onClick={() => handleAction("unsuspend", { suspended: false })} disabled={!!actionLoading} data-testid="admin-unsuspend-btn">
              Reactivar organización
            </Button>
          ) : (
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleAction("suspend", { suspended: true, suspension_reason: "Suspended by admin" })} disabled={!!actionLoading} data-testid="admin-suspend-btn">
              <Ban className="w-3 h-3 mr-1" /> Suspender
            </Button>
          )}

          <Button
            variant="outline"
            className="border-green-200 text-green-700 hover:bg-green-50"
            onClick={() => { setOverridePlan(org.plan_override || ""); setShowOverrideModal(true); }}
            disabled={!!actionLoading}
            data-testid="admin-override-btn"
          >
            <ShieldCheck className="w-3 h-3 mr-1" />
            {hasOverride ? "Editar acceso manual" : "Dar acceso manual"}
          </Button>

          {hasOverride && (
            <Button
              variant="outline"
              className="border-orange-200 text-orange-600 hover:bg-orange-50"
              onClick={() => handleAction("clear_override", { plan_override: "" })}
              disabled={!!actionLoading}
              data-testid="admin-clear-override-btn"
            >
              Quitar override
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
    </div>
  );
}